import { useState, useEffect } from 'react';
import FlexChatIntegration from 'views/ServicePanel/components/QueueContent/components/Contact/service/FlexChatIntegration';
import { toast } from 'react-toastify';

/**
 * @typedef {Object} Channel
 * @property {string} id
 * @property {string} channel
 * @property {Object} openingHours
 * @property {Object} openingHours.ontime
 * @property {string} openingHours.ontime.type
 * @property {string} openingHours.ontime.id
 * @property {string} openingHours.ontime._id
 * @property {Object} openingHours.offtime
 * @property {string} openingHours.offtime.type
 * @property {string} openingHours.offtime.id
 * @property {string} openingHours.offtime._id
 * @property {Object} openingHours.holiday
 * @property {string} openingHours.holiday.type
 * @property {string} openingHours.holiday.id
 * @property {string} openingHours.holiday._id
 * @property {string} nome_canal
 * @property {string} descricao
 * @property {string} [tokenFacebook]
 * @property {string} [facebook_access_token]
 * @property {string} [apikey]
 * @property {string} [source]
 * @property {string} [srcName]
 * @property {string} [tokenTelegram]
 * @property {string} [zenvia_access_token]
 * @property {string} [zenvia_key_pass]
 * @property {string} [broker]
 * @property {string} [username]
 * @property {string} [password]
 * @property {string} [rcsAccountNumber]
 * @property {boolean} usaBot
 * @property {boolean} ativado
 * @property {string} [skillWatson]
 * @property {number} [custoPorConversa]
 * @property {number} [cotacaoDolar]
 */

/**
 * Hook personalizado para buscar e filtrar canais.
 * @param {Object} params - Os parâmetros para buscar e filtrar canais.
 * @param {boolean} [params.showDisabled=false] - Se deve incluir canais desativados.
 * @param {string[]} params.names - Os tipos dos canais a serem buscados.
 * @returns {Channel[]} A lista filtrada de canais.
 *
 * @example
 *
 * const MyComponent = () => {
 *   const channels = useChannels({ showDisabled: true, names: ['whatsapp', 'telegram'] });
 *
 *   return (
 *     <div>
 *       {channels.map(channel => (
 *         <div key={channel.id}>
 *           <h2>{channel.nome_canal}</h2>
 *         </div>
 *       ))}
 *     </div>
 *   );
 * };
 */
const useChannels = ({ showDisabled = false, names = [] }) => {
  const [channels, setChannels] = useState([]);

  useEffect(() => {
    const fetchChannels = async () => {
      try {
        const serviceResponse = await FlexChatIntegration.getAllChannels();
        const data = serviceResponse.data.data;

        const rawChannels = Object.entries(data)
          .filter(([key]) => names.includes(key))
          .flatMap(([, value]) => (Array.isArray(value) ? value : [value]))
          .filter(Boolean);

        const filteredChannels = rawChannels.filter(
          (canal) => showDisabled || canal.ativado === true
        );

        setChannels(filteredChannels);
      } catch (err) {
        toast.error('Não foi possível buscar os canais para filtragem', {
          autoClose: 3000,
          closeOnClick: true
        });
        console.error('Erro ao buscar canais:', err);
      }
    };

    fetchChannels();
  }, [showDisabled, JSON.stringify(names)]);

  return channels ?? [];
};

export default useChannels;
